import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

interface WishlistContextType {
  wishlistItems: string[]; // product IDs
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string) => void;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType>({
  wishlistItems: [],
  isInWishlist: () => false,
  toggleWishlist: () => {},
  loading: false,
});

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [wishlistId, setWishlistId] = useState<string | null>(null);
  const [wishlistItems, setWishlistItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch or create wishlist + items
  useEffect(() => {
    if (!user) {
      setWishlistId(null);
      setWishlistItems([]);
      return;
    }

    const init = async () => {
      setLoading(true);
      // Get existing wishlist
      let { data: wl } = await supabase
        .from("wishlists")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!wl) {
        const { data: created } = await supabase
          .from("wishlists")
          .insert({ user_id: user.id })
          .select("id")
          .single();
        wl = created;
      }

      if (wl) {
        setWishlistId(wl.id);
        const { data: items } = await supabase
          .from("wishlist_items")
          .select("product_id")
          .eq("wishlist_id", wl.id);
        setWishlistItems((items || []).map((i) => i.product_id));
      }
      setLoading(false);
    };

    init();
  }, [user]);

  const isInWishlist = useCallback(
    (productId: string) => wishlistItems.includes(productId),
    [wishlistItems]
  );

  const toggleWishlist = useCallback(
    async (productId: string) => {
      if (!user) {
        toast({ title: "Please log in", description: "Sign in to save items to your wishlist.", variant: "destructive" });
        return;
      }
      if (!wishlistId) return;

      if (wishlistItems.includes(productId)) {
        // Remove
        setWishlistItems((prev) => prev.filter((id) => id !== productId));
        await supabase
          .from("wishlist_items")
          .delete()
          .eq("wishlist_id", wishlistId)
          .eq("product_id", productId);
        toast({ title: "Removed from wishlist" });
      } else {
        // Add
        setWishlistItems((prev) => [...prev, productId]);
        await supabase
          .from("wishlist_items")
          .insert({ wishlist_id: wishlistId, product_id: productId });
        toast({ title: "Added to wishlist ❤️" });
      }
    },
    [user, wishlistId, wishlistItems]
  );

  return (
    <WishlistContext.Provider value={{ wishlistItems, isInWishlist, toggleWishlist, loading }}>
      {children}
    </WishlistContext.Provider>
  );
};
